import { toast } from 'react-toastify';
import { getFirestore } from 'redux-firestore';

import firebase from '../../Firebase/Firebase';
import * as actions from './actionTypes';
import { setLoading } from './demandActions';
import { getProjectImages } from '../../Utils/legacyData';
import { acceptContractSecurely as acceptContractPaymentReady, submitProtectedContract } from '../../Services/marketplacePayments';
import {
  decideDemandOffer,
  answerDemandOfferClarification as answerDemandOfferClarificationSecurely,
  performDemandAction,
  submitDemandOffer,
  withdrawDemandOfferSecurely,
} from '../../Services/demandLifecycle';
import { isArtisanApproved } from '../../Utils/artisanStatus';
import {
  makePortfolioMediaId,
  normalizePortfolioMediaItem,
  normalizePortfolioPhase,
  PORTFOLIO_MEDIA_SOURCE,
} from '../../Utils/portfolio';

const errorMessage = (error) => {
  const code = String(error?.code || '').toLowerCase();
  const raw = String(error?.message || '').trim();
  if (code.includes('permission-denied')) return 'Cette action n’est pas autorisée pour votre compte ou à cette étape du projet.';
  if (code.includes('unauthenticated')) return 'Votre session a expiré. Reconnectez-vous puis réessayez.';
  if (code.includes('failed-precondition') || code.includes('invalid-argument')) {
    return raw && !/^internal$/i.test(raw) ? raw : 'Cette action n’est plus disponible avec les informations actuelles.';
  }
  if (code.includes('not-found')) return 'Le projet ou la proposition n’existe plus. Rechargez la page.';
  if (code.includes('internal') || /^internal$/i.test(raw)) {
    return 'Le service LPB a rencontré une erreur technique. Rechargez la page puis réessayez après le redéploiement complet des fonctions.';
  }
  if (code.includes('unavailable')) return raw && !/^internal$/i.test(raw) ? raw : 'Le service LPB est momentanément indisponible. Réessayez dans quelques instants.';
  return raw || 'La mise à jour du projet a échoué.';
};

const runUpdate = async (dispatch, operation, successMessage) => {
  dispatch(setLoading(true));
  try {
    await operation();
    if (successMessage) toast.success(successMessage);
    return true;
  } catch (error) {
    const message = errorMessage(error);
    dispatch({ type: actions.GET_DEMANDS_FAIL, payload: message });
    toast.error(message);
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

const demandRef = (id) => {
  if (!id) throw new Error('Identifiant de demande manquant.');
  return getFirestore().collection('demands').doc(id);
};

export function addEstimation(payload) {
  return (dispatch, getState) => runUpdate(
    dispatch,
    async () => {
      const profile = getState().firebase?.profile || {};
      if (!isArtisanApproved(profile.registration_status)) {
        throw new Error('Votre profil doit être validé avant l’envoi d’une proposition.');
      }
      await submitDemandOffer({
        demandId: payload.demandId,
        offer: {
          contactNumber: payload.contactNumber,
          descriptionOfEstimation: payload.descriptionOfEstimation,
          visitDateEstimation: payload.visitDateEstimation,
          assessmentMode: payload.assessmentMode,
          additionalInfoRequest: payload.additionalInfoRequest,
          materialCostEstim: payload.materialCostEstim,
          wageEstim: payload.wageEstim,
          estimatedBudget: payload.estimatedBudget,
        },
      });
    },
    'Votre proposition a été envoyée. Vous pouvez la suivre dans « Mes missions ».',
  );
}

export function acceptDemandOffer(demand, offer) {
  return (dispatch) => runUpdate(
    dispatch,
    () => decideDemandOffer({ demandId: demand.demand_id, artisanId: offer.artisan_id, decision: 'accept' }),
    'La proposition a été acceptée. L’artisan la retrouve maintenant dans ses missions.',
  );
}

export function declineDemandOffer(demandId, artisanId) {
  return (dispatch) => runUpdate(
    dispatch,
    () => decideDemandOffer({ demandId, artisanId, decision: 'decline' }),
    'La proposition a été refusée.',
  );
}

export function answerDemandOfferClarification(demandId, artisanId, response) {
  return (dispatch) => runUpdate(
    dispatch,
    () => answerDemandOfferClarificationSecurely({ demandId, artisanId, response }),
    'Votre réponse a été envoyée à l’artisan. La demande reste ouverte aux autres propositions.',
  );
}

export function withdrawDemandOffer(demandId) {
  return (dispatch) => runUpdate(
    dispatch,
    () => withdrawDemandOfferSecurely({ demandId }),
    'Votre proposition a été retirée.',
  );
}

const projectAction = (dispatch, demandId, action, payload, successMessage) => runUpdate(
  dispatch,
  () => performDemandAction({ demandId, action, payload }),
  successMessage,
);

export function acceptEstimation(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'accept_estimation', {}, 'L’estimation a été acceptée.');
}

export function rejectEstimation(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'reject_estimation', {}, 'L’estimation a été refusée.');
}

export function confirmVisitByArtisan(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'confirm_visit_artisan', {}, 'Votre présence à la visite a été confirmée. Le client peut maintenant la valider.');
}

export function markAssessmentReadyByArtisan(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'mark_assessment_ready', {}, 'Vous pouvez maintenant préparer le devis détaillé.');
}

export function validateTheVisit(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'confirm_visit_client', {}, 'La visite a été confirmée par les deux parties.');
}

export function refuseTheVisit(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'refuse_visit', {}, 'La visite a été refusée.');
}

export function sendContract(contract) {
  return (dispatch) => runUpdate(
    dispatch,
    () => submitProtectedContract({ demandId: contract.demandId, contract }),
    'Le devis protégé a été envoyé au client.',
  );
}

export function acceptContractClient(demand, consent) {
  return (dispatch) => runUpdate(
    dispatch,
    () => acceptContractPaymentReady({ demandId: demand.demand_id, consent }),
    'Le devis a été accepté et votre preuve d’accord a été enregistrée. Vous pouvez maintenant régler l’acompte.',
  );
}

export function rejectContractClient(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'reject_contract', {}, 'Le devis a été refusé.');
}

export function askModificationClient(demand) {
  return (dispatch) => projectAction(
    dispatch,
    demand.demand_id,
    'request_contract_modification',
    { modification: demand.modification || '' },
    'Votre demande de modification a été envoyée.',
  );
}

export function sendModificaionArtisan(contract) {
  return (dispatch) => runUpdate(
    dispatch,
    () => submitProtectedContract({
      demandId: contract.demandId,
      contract: {
        ...contract,
        modificationAsked: contract.modificationAsked || '',
        modificationResponse: contract.modificationResponse || '',
      },
    }),
    'Le devis modifié a été renvoyé au client.',
  );
}

export function finalizeJobArtisan(payload) {
  return (dispatch) => projectAction(
    dispatch,
    payload.demandId,
    'declare_completion',
    { note: payload.additionalInfo || '' },
    'La fin des travaux a été déclarée. Le client doit maintenant confirmer le chantier.',
  );
}

export function resolveCompletionIssueArtisan(demand, note = '') {
  return (dispatch) => projectAction(
    dispatch,
    demand.demand_id,
    'resolve_completion_issue',
    { note },
    'La correction a été déclarée. Le client peut vérifier et confirmer à nouveau.',
  );
}

export function confirmCompletionClient(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'confirm_completion', {}, 'La fin des travaux est confirmée. Le solde peut maintenant être réglé.');
}

export function reportCompletionIssue(demand, reason) {
  return (dispatch) => projectAction(
    dispatch,
    demand.demand_id,
    'report_completion_issue',
    { reason },
    'Le problème a été signalé. Échangez avec l’artisan avant de confirmer le solde.',
  );
}

export function deletOfferArtisanAction(demand) {
  return (dispatch) => projectAction(dispatch, demand.demand_id, 'withdraw_selected_artisan', {}, 'Votre proposition a été retirée.');
}

export function reviewArtisanAPI(review) {
  // Compatibility wrapper: all reviews must pass through the verified,
  // double-blind backend pipeline. Direct client writes are intentionally gone.
  return (dispatch) => runUpdate(
    dispatch,
    async () => {
      const callable = firebase.app().functions('europe-west1').httpsCallable('submitVerifiedReview');
      await callable({
        demandId: review.demandId,
        rating: Number(review.rating),
        comment: String(review.reviewText || '').trim(),
        dimensions: review.dimensions || {},
      });
    },
    'Merci, votre avis vérifié a été enregistré.',
  );
}

const sanitizePortfolioMedia = (item, index = 0) => {
  const normalized = normalizePortfolioMediaItem(item, item?.source, index);
  if (!normalized) return null;

  return {
    id: normalized.id,
    url: normalized.url,
    source: normalized.source,
    phase: normalizePortfolioPhase(normalized.phase),
    storage_path: normalized.storage_path || '',
    original_name: normalized.original_name || '',
    uploaded_at: normalized.uploaded_at || null,
  };
};

export function savePortfolioProject(payload) {
  return (dispatch) => runUpdate(
    dispatch,
    async () => {
      const db = getFirestore();
      const targetRef = demandRef(payload.demandId);
      const status = ['draft', 'published', 'hidden'].includes(payload.status)
        ? payload.status
        : 'draft';
      const featured = status === 'published' && Boolean(payload.featured);

      const targetSnapshot = await targetRef.get();
      if (!targetSnapshot.exists) throw new Error('Ce projet n’existe plus.');

      const demand = targetSnapshot.data();
      if (demand?.contract?.artisan_id !== payload.artisanId) {
        throw new Error('Vous ne pouvez modifier que vos propres réalisations.');
      }
      if (demand?.demand_status !== 'reviewed') {
        throw new Error('Seuls les projets terminés et évalués peuvent être ajoutés à la vitrine.');
      }

      const submittedMedia = Array.isArray(payload.media)
        ? payload.media.map(sanitizePortfolioMedia).filter(Boolean)
        : [];
      const submittedByUrl = new Map(submittedMedia.map((item) => [item.url, item]));

      // Client photos are contractual source material: they are always preserved in the portfolio data.
      const clientMedia = getProjectImages(demand).map((url, index) => {
        const submitted = submittedByUrl.get(url);
        return sanitizePortfolioMedia({
          ...(submitted || {}),
          id: submitted?.id || makePortfolioMediaId(PORTFOLIO_MEDIA_SOURCE.CLIENT, url, index),
          url,
          source: PORTFOLIO_MEDIA_SOURCE.CLIENT,
        }, index);
      }).filter(Boolean);

      const clientUrls = new Set(clientMedia.map((item) => item.url));
      const artisanMedia = submittedMedia.filter((item) => (
        item.source === PORTFOLIO_MEDIA_SOURCE.ARTISAN
        && !clientUrls.has(item.url)
      ));
      const media = [...clientMedia, ...artisanMedia].filter((item, index, values) => (
        values.findIndex((candidate) => candidate.url === item.url) === index
      ));

      const mediaUrls = media.map((item) => item.url);
      const coverImage = mediaUrls.includes(payload.coverImage)
        ? payload.coverImage
        : mediaUrls[0] || '';

      if (status === 'published' && media.length === 0) {
        throw new Error('Ajoutez au moins une photo avant de publier ce projet.');
      }

      const beforeAfterInput = payload.beforeAfter || {};
      const beforeMedia = media.find((item) => item.id === beforeAfterInput.beforeId);
      const afterMedia = media.find((item) => item.id === beforeAfterInput.afterId);
      const beforeAfter = beforeAfterInput.enabled && beforeMedia && afterMedia && beforeMedia.id !== afterMedia.id
        ? {
          enabled: true,
          title: String(beforeAfterInput.title || 'Avant / Après').trim().slice(0, 80),
          before_id: beforeMedia.id,
          before_url: beforeMedia.url,
          after_id: afterMedia.id,
          after_url: afterMedia.url,
        }
        : null;

      const title = String(payload.title || '').trim();
      const summary = String(payload.summary || '').trim();
      if (!title) throw new Error('Ajoutez un titre public à cette réalisation.');
      if (!summary) throw new Error('Ajoutez un résumé public à cette réalisation.');

      const currentPortfolio = demand.portfolio || {};
      const portfolio = {
        status,
        title,
        summary,
        category: String(payload.category || '').trim(),
        city: String(payload.city || '').trim(),
        duration: String(payload.duration || '').trim(),
        techniques: String(payload.techniques || '').trim(),
        result: String(payload.result || '').trim(),
        price_range: String(payload.priceRange || '').trim(),
        media,
        custom_images: artisanMedia,
        // Retained for compatibility with older screens and previously deployed versions.
        selected_images: mediaUrls,
        cover_image: coverImage,
        before_after: beforeAfter,
        featured,
        updated_at: firebase.firestore.Timestamp.now(),
        published_at: status === 'published'
          ? currentPortfolio.published_at || firebase.firestore.Timestamp.now()
          : currentPortfolio.published_at || null,
      };

      const batch = db.batch();

      if (featured) {
        const projectsSnapshot = await db
          .collection('demands')
          .where('contract.artisan_id', '==', payload.artisanId)
          .get();

        projectsSnapshot.docs.forEach((doc) => {
          if (doc.id !== payload.demandId && doc.data()?.portfolio?.featured) {
            batch.update(doc.ref, {
              'portfolio.featured': false,
              'portfolio.updated_at': firebase.firestore.Timestamp.now(),
            });
          }
        });
      }

      batch.update(targetRef, {
        portfolio,
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
      await batch.commit();
    },
    payload.status === 'published'
      ? 'Le projet est publié dans votre vitrine.'
      : 'La vitrine a été mise à jour.',
  );
}

export function setPortfolioProjectStatus(payload) {
  return (dispatch) => runUpdate(
    dispatch,
    async () => {
      const status = ['draft', 'published', 'hidden'].includes(payload.status)
        ? payload.status
        : 'hidden';
      const ref = demandRef(payload.demandId);
      const snapshot = await ref.get();
      if (!snapshot.exists) throw new Error('Ce projet n’existe plus.');
      const demand = snapshot.data();

      if (demand?.contract?.artisan_id !== payload.artisanId) {
        throw new Error('Vous ne pouvez modifier que vos propres réalisations.');
      }

      const currentPortfolio = demand.portfolio || {};
      const hasPortfolioMedia = (Array.isArray(currentPortfolio.media) && currentPortfolio.media.length > 0)
        || (Array.isArray(currentPortfolio.selected_images) && currentPortfolio.selected_images.length > 0);
      if (status === 'published' && !hasPortfolioMedia) {
        throw new Error('Ouvrez l’éditeur et ajoutez au moins une photo avant publication.');
      }

      await ref.update({
        'portfolio.status': status,
        'portfolio.featured': status === 'published' && Boolean(currentPortfolio.featured),
        'portfolio.published_at': status === 'published'
          ? currentPortfolio.published_at || firebase.firestore.Timestamp.now()
          : currentPortfolio.published_at || null,
        'portfolio.updated_at': firebase.firestore.Timestamp.now(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
    },
    payload.status === 'published'
      ? 'Le projet est maintenant visible dans votre vitrine.'
      : payload.status === 'hidden'
        ? 'Le projet a été masqué de votre vitrine.'
        : 'Le projet est enregistré comme brouillon.',
  );
}
